import React, { ReactNode } from 'react';
import { loadLanguagePack } from '@americanexpress/one-app-ducks';
import { OneFetchyeProvider } from 'fetchye-one-app';
import oneAppModuleWrapper, {
  OneAppModuleWrapperProps,
} from '@americanexpress/one-app-module-wrapper';
import DataManager from '@americanexpress/data-manager';
import { OneExperimentsProvider } from '@americanexpress/one-experiments';
import { AmexDDLRegister, getAmexDataManagerConfig } from '@americanexpress/data-manager-utils';
import SharedScript, { Adobe, Contentsquare } from '@americanexpress/shared-scripts';
import { compose } from 'redux';
import Helmet from 'react-helmet';
import { IntlProvider } from 'react-intl';
import type { OneAppV5RootModule } from 'one-app-types';
import { LoadModuleData } from 'holocron';
import { reducer } from '../ducks';
import childRoutes from '../childRoutes';
import { getPageTitle } from '../utils/getPageTitle';
import type { RoutePropsWithTitle } from '../types';
import ContentSquareScript from './ContentSquareScript';
import { isAuthenticatedRoute } from '../utils/isAuthenticatedRoute';

type Props = {
  children: ReactNode;
  routes: RoutePropsWithTitle[];
  location: Location;
} & OneAppModuleWrapperProps;

export const CommercialExperienceRoot: OneAppV5RootModule<Props> = (props) => {
  const { locale, languageData, children, routes, location } = props;

  React.useEffect(() => {
    const IntlWithPolyfill = Intl as typeof Intl & { __disableRegExpRestore: () => void };
    // eslint-disable-next-line no-underscore-dangle -- cannot change variable name
    IntlWithPolyfill.__disableRegExpRestore();
  }, []);

  const { pathname } = location;
  React.useLayoutEffect(() => {
    if (global.BROWSER) {
      window.scrollTo(0, 0);
    }
  }, [pathname]);

  return (
    <div>
      <Helmet>
        <meta charSet="utf-8" />
        <html lang={locale} />
        <title>{getPageTitle(routes, languageData)}</title>
        <meta httpEquiv="X-UA-Compatible" content="IE=edge" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta name="application-name" content="American Express" />
        <body className="dls-gray-01-bg" />
        <link rel="icon" href="https://www.americanexpress.com/favicon.ico" />
      </Helmet>
      {}
      <DataManager
        {...getAmexDataManagerConfig({
          initialDDL: {
            ddl: {
              view: { locale: 'en-US' },
            },
          },
        })}
      >
        <AmexDDLRegister />
        <SharedScript name="one-stream-data-handler" windowCheck="oneStreamData" version="^0.1.2" />
        <SharedScript name="one-identity-session" version="^1.0.0" windowCheck="AmexSession" />
        <Contentsquare version="^1.0.0" />
        <Adobe version="^0.1.0" />
        <IntlProvider locale={locale} messages={languageData}>
          <OneExperimentsProvider>
            <OneFetchyeProvider>
              {isAuthenticatedRoute(location.pathname) ? (
                <ContentSquareScript>{children}</ContentSquareScript>
              ) : (
                children
              )}
            </OneFetchyeProvider>
          </OneExperimentsProvider>
        </IntlProvider>
      </DataManager>
    </div>
  );
};

CommercialExperienceRoot.childRoutes = childRoutes;

export const loadModuleData: LoadModuleData<Props> = async ({ store }) => {
  const { dispatch } = store;
  await dispatch(loadLanguagePack('commercial-experience-root', { fallbackLocale: 'en-US' }));
};

CommercialExperienceRoot.holocron = {
  name: 'commercial-experience-root',
  loadModuleData,
  reducer,
};

/* istanbul ignore next */
if (!global.BROWSER) {
  // eslint-disable-next-line global-require, @typescript-eslint/no-var-requires -- require needs to be inside browser check,
  CommercialExperienceRoot.appConfig = require('../appConfig').default;
}

const hocChain = compose(oneAppModuleWrapper('commercial-experience-root'));

export default hocChain(CommercialExperienceRoot);
